import { CheckOutlined, UserAddOutlined } from '@ant-design/icons';
import { Form, Input, message, Modal, Spin, Table } from 'antd';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { AuthContext } from '../../Guard/AuthContext';
import Button from '../../components/Button/Button';
import ContactsTour from '../../components/ContactsTour/ContactsTour';
import InstructionsBadge from '../../components/InstructionsBadge/InstructionsBadge';
import { useTour } from '../../contexts/TourContext';
import { useContactStore } from '../../hooks/useContactStore';
import { apiInstance } from '../../utils/api';

const FREE_STATUS_ID = 'df17a6d7-2510-4a1e-9692-f9c34d66b156';
const BLOCKED_STATUS_ID = '25405d2a-9df4-47a9-9313-fc8b4ca329fa';
const IGNORED_REQUEST_STATUSES = [
  '6e5f4218-f46b-1410-fe9a-0050ba5d6c38',
  '3e7f420c-f46b-1410-fc9a-0050ba5d6c38',
];

const getContactDisplayName = (contact) => {
  if (!contact) return '';
  const name = (contact.RespName || contact.Name || '').trim();
  const surname = (contact.RespSurName || contact.Surname || '').trim();
  if (!surname) return name;
  if (!name) return surname;
  const nameLower = name.toLowerCase();
  const surnameLower = surname.toLowerCase();
  if (nameLower.includes(surnameLower)) return name;
  if (surnameLower.includes(nameLower)) return surname;
  return `${name} ${surname}`.trim();
};

const checkHasActiveCase = (contact, casesList) => {
  if (!contact?.Id || !Array.isArray(casesList)) return false;
  const contactIdLower = contact.Id.toLowerCase();
  const displayNameLower = getContactDisplayName(contact).toLowerCase();
  const nameOnlyLower = (contact.Name || contact.RespName || '').trim().toLowerCase();

  return casesList.some((caseItem) => {
    const statusVal = caseItem?.Status?.value;
    if (!statusVal || IGNORED_REQUEST_STATUSES.includes(statusVal)) {
      return false;
    }

    const receiverVal = typeof caseItem?.RespCardReceiver === 'string'
      ? caseItem.RespCardReceiver
      : caseItem?.RespCardReceiver?.value;
    if (receiverVal && receiverVal.toLowerCase() === contactIdLower) return true;

    const contactVal = typeof caseItem?.Contact === 'string'
      ? caseItem.Contact
      : caseItem?.Contact?.value;
    if (contactVal && contactVal.toLowerCase() === contactIdLower) return true;

    if (Array.isArray(caseItem?.RespCardReceivers)) {
      if (caseItem.RespCardReceivers.some((id) => typeof id === 'string' && id.toLowerCase() === contactIdLower)) {
        return true;
      }
    }

    if (Array.isArray(caseItem?.RespCardInCase)) {
      if (caseItem.RespCardInCase.some((c) => c?.id && c.id.toLowerCase() === contactIdLower)) {
        return true;
      }
    }

    if (caseItem?.Subject) {
      const subjectLower = caseItem.Subject.toLowerCase();
      if (subjectLower.includes('скд')) {
        if (displayNameLower && subjectLower.includes(displayNameLower)) return true;
        if (nameOnlyLower && subjectLower.includes(nameOnlyLower)) return true;
      }
    }

    return false;
  });
};

const ContactsTable = () => {
  const { push } = useHistory();
  const { currentContactStore } = useContactStore();
  const { user, setUser, setIsStoreSelectDisabled } = useContext(AuthContext);
  const { isTourActive, startTour } = useTour();

  const [searchText, setSearchText] = useState('');
  const [contacts, setContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [cards, setCards] = useState([]);
  const [cases, setCases] = useState([]);
  const [reference, setReference] = useState(null);
  const [instructionsRead, setInstructionsRead] = useState(user?.instructionsRead || false);

  // Масове замовлення СКД
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [isOrderModalVisible, setIsOrderModalVisible] = useState(false);
  const [isOrderLoading, setIsOrderLoading] = useState(false);
  const [isUnassignedWarningVisible, setIsUnassignedWarningVisible] = useState(false);
  const [pendingOrderContacts, setPendingOrderContacts] = useState([]);

  // Блокування селектора торгових точок в режимі вибору
  useEffect(() => {
    if (setIsStoreSelectDisabled) {
      setIsStoreSelectDisabled(isSelectionMode);
    }
    return () => {
      if (setIsStoreSelectDisabled) {
        setIsStoreSelectDisabled(false);
      }
    };
  }, [isSelectionMode, setIsStoreSelectDisabled]);

  // Синхронізація з user при зміні
  useEffect(() => {
    if (user?.instructionsRead !== undefined) {
      setInstructionsRead(user.instructionsRead);
    }
  }, [user?.instructionsRead]);

  // Функція для відмітки інструкції як прочитаної
  const markInstructionsAsRead = async () => {
    try {
      await apiInstance(`${process.env.REACT_APP_API_URL}/api/users/${user.id}`, 'PUT', {
        instructionsRead: true,
      });
      setInstructionsRead(true);
      setUser({ ...user, instructionsRead: true });
      message.success('Інструкцію відмічено як прочитану');
    } catch (error) {
      console.error('Помилка оновлення статусу інструкції:', error);
      message.error('Не вдалося оновити статус інструкції');
    }
  };

  const fetchContacts = async () => {
    setLoadingContacts(true);
    try {
      const contactsPromise = apiInstance(
        `${process.env.REACT_APP_API_URL}/api/crm/get-data?type=contacts`,
        'GET'
      );
      const cardsPromise = apiInstance(
        `${process.env.REACT_APP_API_URL}/api/crm/get-data?type=cards`,
        'GET'
      );
      const casesPromise = apiInstance(
        `${process.env.REACT_APP_API_URL}/api/crm/get-data?type=cases`,
        'GET'
      ).catch(() => ({ rows: [] }));

      const [contactsResponse, cardsResponse, casesResponse, referencesResponse] = await Promise.all([
        contactsPromise,
        cardsPromise,
        casesPromise,
        apiInstance(`${process.env.REACT_APP_API_URL}/api/requests/references`, 'GET').catch(() => null),
      ]);

      if (referencesResponse) setReference(referencesResponse);

      const contactsRaw = contactsResponse.rows || [];
      const cardsRaw = cardsResponse.rows || [];

      setCards(cardsRaw);
      setCases(casesResponse.rows || []);

      const cardsById = cardsRaw.reduce((acc, card) => {
        if (card?.Id) {
          acc.set(card.Id, card);
        }
        return acc;
      }, new Map());

      // Точечный запрос карт по contactId в параллели для 100% точности (как в карточке контакта)
      const contactCardsPromises = contactsRaw.map((contact) =>
        apiInstance(
          `${process.env.REACT_APP_API_URL}/api/crm/get-data?type=contact-card&contactId=${contact.Id}`,
          'GET'
        )
          .then((res) => (res.rows && res.rows.length ? res.rows[0] : null))
          .catch(() => null)
      );

      const contactCards = await Promise.all(contactCardsPromises);
      const contactCardsMap = new Map();
      contactsRaw.forEach((contact, idx) => {
        const cCard = contactCards[idx];
        if (cCard) {
          contactCardsMap.set(contact.Id.toLowerCase(), cCard);
        }
      });

      const normalizeCardLink = (cardLike) => {
        if (!cardLike) return { id: '', number: '' };
        if (typeof cardLike === 'string') {
          return { id: cardLike, number: '' };
        }
        if (typeof cardLike === 'object') {
          return {
            id: cardLike.value || cardLike.Id || '',
            number: cardLike.displayValue || cardLike.Name || cardLike.cardNumber || '',
          };
        }
        return { id: '', number: '' };
      };

      const contactsWithCards = contactsRaw.map((contact) => {
        const { id: linkedCardId, number: linkedCardNumber } = normalizeCardLink(
          contact?.RespCardSCA
        );

        // Сначала берем карту из точечного запроса (100% сопоставление)
        let card = contactCardsMap.get(contact.Id.toLowerCase());

        // Если точечный запрос ничего не вернул, пробуем найти по обратной связи в cardsRaw
        if (!card) {
          const matchedCardByContact = cardsRaw.find(
            (cardItem) =>
              cardItem?.RespContact?.value &&
              contact.Id &&
              cardItem.RespContact.value.toLowerCase() === contact.Id.toLowerCase()
          );
          card = matchedCardByContact || (linkedCardId ? cardsById.get(linkedCardId) : undefined);
        }

        const isBlocked = card?.RespSCAStatus?.value === '25405d2a-9df4-47a9-9313-fc8b4ca329fa';
        const isFree = card?.RespSCAStatus?.value === 'df17a6d7-2510-4a1e-9692-f9c34d66b156';
        const isNotAssigned = !card || isBlocked || isFree || !card?.RespSCAStatus?.value;

        if (isNotAssigned) {
          card = undefined;
        }
        const cardStatus = card?.RespSCAStatus || null;

        const jobTitle =
          contact?.RespEmployeeJobId?.displayValue ||
          contact?.RespEmployeeJob?.displayValue ||
          contact?.RespEmployeeJob?.Name ||
          contact?.RespEmployeeJob?.name ||
          contact?.Job?.displayValue ||
          contact?.Job?.Name ||
          contact?.Job?.name ||
          '';

        return {
          ...contact,
          RespDisplayName: getContactDisplayName(contact),
          RespName: contact.RespName || contact.Name || '',
          RespSurName: contact.Surname || contact.RespSurName || '',
          RespMobilePhone: contact.MobilePhone || contact.RespMobilePhone || '',
          RespCardNumber: isNotAssigned
            ? ''
            : (card?.Name && card.Name !== 'Картка СКД'
               ? card.Name
               : (linkedCardNumber && linkedCardNumber !== 'Картка СКД' ? linkedCardNumber : '')),
          RespCardId: isNotAssigned ? '' : (card?.Id || linkedCardId || ''),
          RespCardStatus: cardStatus,
          RespEmployeeJobTitle: jobTitle,
        };
      });

      // Фільтруємо контакти без імені (RespName або Name)
      const contactsWithNames = contactsWithCards.filter((contact) => {
        const name = (contact.RespDisplayName || contact.RespName)?.trim();
        return name && name.length > 0;
      });

      setContacts(contactsWithNames);
    } catch (error) {
      console.error('Ошибка получения данных:', error);
    } finally {
      setLoadingContacts(false);
    }
  };

  useEffect(() => {
    if (currentContactStore?.crmId) {
      fetchContacts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentContactStore?.crmId]);

  const filteredContacts = useMemo(() => {
    if (!searchText) return contacts;

    const searchLower = searchText.toLowerCase();
    return contacts.filter((contact) => {
      const fullName = (contact.RespDisplayName || getContactDisplayName(contact)).toLowerCase();
      const phone = (contact.RespMobilePhone || contact.MobilePhone || '').toLowerCase();
      const jobTitle = (contact.RespEmployeeJobTitle || '').toLowerCase();
      return (
        fullName.includes(searchLower) ||
        phone.includes(searchLower) ||
        jobTitle.includes(searchLower)
      );
    });
  }, [contacts, searchText]);

  const targetCount = cards.filter((card) => card.RespSCAStatus?.value === FREE_STATUS_ID).length;
  const allCards = cards.filter((card) => card.RespSCAStatus?.value !== BLOCKED_STATUS_ID).length;

  // Нерозподілені вільні картки (без прив'язки до контакту)
  const unassignedFreeCards = useMemo(
    () => cards.filter((card) => card.RespSCAStatus?.value === FREE_STATUS_ID && !card.RespContact?.value),
    [cards]
  );

  // Ключі сотрудників БЕЗ картки і БЕЗ активного замовлення
  const withoutCardKeys = useMemo(
    () =>
      filteredContacts
        .filter((contact) => {
          if (contact.RespCardNumber) return false;
          const hasActiveCase = checkHasActiveCase(contact, cases);
          return !hasActiveCase;
        })
        .map((c) => c.Id),
    [filteredContacts, cases]
  );

  // Кастомний заголовок чекбокс-колонки: вибирає лише тих, у кого немає картки
  const withoutCardChecked =
    withoutCardKeys.length > 0 &&
    withoutCardKeys.every((k) => selectedRowKeys.includes(k));

  const handleToggleSelectWithoutCard = () => {
    if (withoutCardChecked) {
      // зняти тільки тих, хто без картки (решта залишаються)
      setSelectedRowKeys((prev) => prev.filter((k) => !withoutCardKeys.includes(k)));
    } else {
      setSelectedRowKeys((prev) => Array.from(new Set([...prev, ...withoutCardKeys])));
    }
  };

  // rowSelection — активний тільки в режимі вибору
  const rowSelection = isSelectionMode
    ? {
        selectedRowKeys,
        onChange: (newSelectedKeys) => setSelectedRowKeys(newSelectedKeys),
        getCheckboxProps: (record) => {
          const hasCard = Boolean(record.RespCardNumber);
          const hasActiveCase = checkHasActiveCase(record, cases);
          return {
            disabled: hasCard || hasActiveCase,
          };
        },
        columnTitle: (
          <span
            title={withoutCardChecked ? 'Зняти вибір без картки' : 'Вибрати всіх без картки'}
            style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}
            onClick={handleToggleSelectWithoutCard}
          >
            <input
              type="checkbox"
              readOnly
              checked={withoutCardChecked}
              ref={(el) => { if (el) el.indeterminate = false; }}
              style={{ cursor: 'pointer', width: 16, height: 16 }}
            />
          </span>
        ),
      }
    : undefined;

  // Увійти в режим вибору
  const handleEnterSelectionMode = () => {
    setSelectedRowKeys([]);
    setIsSelectionMode(true);
  };

  // Вийти з режиму вибору
  const handleCancelSelectionMode = () => {
    setIsSelectionMode(false);
    setSelectedRowKeys([]);
  };

  // Підтвердити вибір і відкрити модал
  const handleOpenOrderModal = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('Оберіть хоча б одного співробітника');
      return;
    }
    const selected = contacts.filter((c) => selectedRowKeys.includes(c.Id));
    setPendingOrderContacts(selected);
    if (unassignedFreeCards.length > 0) {
      setIsUnassignedWarningVisible(true);
    } else {
      setIsOrderModalVisible(true);
    }
  };

  const handleUnassignedWarningOk = () => {
    setIsUnassignedWarningVisible(false);
    setIsOrderModalVisible(true);
  };

  // Підтвердити замовлення — 1 звернення на всіх обраних
  const handleConfirmOrder = async () => {
    setIsOrderLoading(true);
    try {
      const names = pendingOrderContacts
        .map((c) => c.RespName || '')
        .filter(Boolean)
        .join(', ');

      await apiInstance(
        `${process.env.REACT_APP_API_URL}/api/post-data?type=create-request`,
        'POST',
        {
          Subject: `Замовлення СКД (${pendingOrderContacts.length} осіб): ${names}`,
          ServiceCategory: 'd948830b-47b7-4479-800e-277e7ba58de1',
          RespCardReceivers: pendingOrderContacts.map((c) => ({
            id: c.Id,
            name: c.RespName || '',
          })),
          SuContractorType: '4857f213-f264-4cc4-99e2-a234e3cb3efb',
          Category: '2a92dbea-2b8c-4a63-b3f6-ea4135f981d7',
          Status: 'ae5f2f10-f46b-1410-fd9a-0050ba5d6c38',
        }
      );

      setIsOrderModalVisible(false);
      setIsSelectionMode(false);
      setSelectedRowKeys([]);

      message.success({
        content: (
          <span>
            Звернення створено для {pendingOrderContacts.length} співробітників. Статус у{' '}
            <span
              onClick={() => (window.location.href = '/requests')}
              style={{ color: '#DB3EB1', cursor: 'pointer', textDecoration: 'underline' }}
            >
              «Зверненнях»
            </span>
          </span>
        ),
        icon: <span style={{ color: '#52c41a', marginRight: 8 }}><CheckOutlined /></span>,
        duration: 6,
      });

      await fetchContacts();
    } catch (error) {
      console.error('Помилка замовлення:', error);
      message.error(error?.message || 'Сталася помилка при створенні звернення');
    } finally {
      setIsOrderLoading(false);
    }
  };

  const formatPhoneNumber = (phoneNumber = '') => {
    if (!phoneNumber) return '';
    const cleaned = phoneNumber.replace(/\D/g, '');
    if (cleaned.length < 12) return phoneNumber;
    return `+${cleaned.slice(0, 2)} (${cleaned.slice(2, 5)}) ${cleaned.slice(5, 8)}-${cleaned.slice(
      8,
      10
    )}-${cleaned.slice(10, 12)}`;
  };

  const columns = [
    {
      title: 'Співробітник',
      key: 'fullName',
      className: 'start',
      width: 150,
      render: (_, record) => (
        <div className="f16-grey-dark">
          <div style={{ fontWeight: '500', fontSize: '16px' }}>{record.RespName || ''}</div>
          {record.RespEmployeeJobTitle ? (
            <div
              style={{
                fontSize: '12px',
                marginTop: '-3px',
                marginLeft: '8px',
                fontStyle: 'italic',
              }}
            >
              {record.RespEmployeeJobTitle}
            </div>
          ) : null}
        </div>
      ),
    },
    {
      title: 'Номер телефону',
      dataIndex: 'RespMobilePhone',
      key: 'RespMobilePhone',
      className: 'center f16-grey-dark',
      width: 150,
      render: (phoneNumber) => <div>{formatPhoneNumber(phoneNumber)}</div>,
    },
    {
      title: 'Номер картки',
      dataIndex: 'RespCardNumber',
      key: 'RespCardNumber',
      className: 'center',
      width: 200,
      render: (cardNumber) => <div className="f16-grey-dark">{cardNumber || 'Не призначено'}</div>,
    },
    {
      title: 'Статус',
      key: 'RespCardStatus',
      className: 'center f16-grey-dark contact-card-status-column',
      width: 170,
      render: (_, record) => {
        const activeCase = checkHasActiveCase(record, cases);

        const status = record?.RespCardStatus;
        const statusConfig = {
          '3075a30b-f6e8-45cd-990b-28399db75400': {
            text: 'Видана',
            color: '#52c41a',
          },
          // 'df17a6d7-2510-4a1e-9692-f9c34d66b156': {
          //   text: 'Готова до видачі',
          //   color: '#1890ff',
          // },
          '2ec4e1b9-b7f5-4eb2-9a83-695c9b1be716': {
            text: 'Готова до видачі',
            color: '#1890ff',
          },
          '25405d2a-9df4-47a9-9313-fc8b4ca329fa': {
            text: 'Заблокована',
            color: '#ff4d4f',
          },
        };

        if (status?.value) {
          const currentStatus = statusConfig[status.value] || {
            text: 'Невідомо',
            color: '#8c8c8c',
          };
          return <div style={{ color: currentStatus.color }}>{currentStatus.text}</div>;
        }

        if (activeCase) {
          return <div style={{ color: '#DB3EB1' }}>Очікуйте</div>;
        }

        return <div style={{ color: '#8c8c8c' }}>Не призначено</div>;
      },
    },
    {
      title: 'Дії з картками',
      key: 'actions',
      className: 'center',
      width: 150,
      render: (_, record) => (
        <Button
          text="Керувати"
          bg="turquoise"
          onClick={(e) => { e.stopPropagation(); push(`/contacts/edit/${record.Id}`); }}
        />
      ),
    },
  ];

  if (loadingContacts) {
    return (
      <div
        style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}
      >
        <Spin size="large" />
      </div>
    );
  }

  const selectedContacts = contacts.filter((c) => selectedRowKeys.includes(c.Id));

  return (
    <div className="employees-page">
      <div className="box header-content">
        <div className="left-section">
          <div className="search-and-filter">
            <Form.Item name="searchContact" style={{ marginBottom: 0, width: 200, flexShrink: 0 }}>
              <Input
                placeholder="Пошук за ПІБ або телефоном"
                className="search-input"
                onChange={(e) => setSearchText(e.target.value)}
              />
            </Form.Item>

            {isSelectionMode ? (
              /* Режим вибору: підказка + Підтвердити + Скасувати */
              <div style={{ marginLeft: '12px', display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 }}>
                <Button
                  text={selectedRowKeys.length > 0 ? `Підтвердити (${selectedRowKeys.length})` : 'Підтвердити'}
                  bg="turquoise"
                  onClick={handleOpenOrderModal}
                  disabled={selectedRowKeys.length === 0}
                />
                <Button
                  text="Скасувати"
                  bg="orange"
                  onClick={handleCancelSelectionMode}
                />
              </div>
            ) : (
              /* Звичайний режим */
              <div style={{ marginLeft: '12px', display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 }}>
                <Button
                  text={
                    <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      Додати
                      <UserAddOutlined />
                    </span>
                  }
                  bg="pink"
                  className="add-employee-btn"
                  onClick={() => push('/contacts/create')}
                />
                <Button
                  text="Замовити СКД"
                  bg="purple"
                  className="order-skd-btn"
                  onClick={handleEnterSelectionMode}
                />
              </div>
            )}

            {!isSelectionMode && (
              <div className="show-desktop" style={{ marginLeft: '16px' }}>
                <InstructionsBadge
                  instructionsRead={instructionsRead}
                  onClick={() => { startTour(); }}
                  alwaysVisible={true}
                />
              </div>
            )}
          </div>
        </div>
        <div className="right-section">
          <div className="cards-available">
            <span className="text-grey-dark-normal-f22-l28">Доступно карток: </span>
            <span className="text-black-bold-f22">
              <span style={{ color: 'rgb(219, 62, 177)' }}>{targetCount}</span> з {allCards}
            </span>
          </div>
        </div>
      </div>

      <div className="box">
        <Table
          className="default-table account-table request-table contacts-clickable-rows"
          dataSource={filteredContacts}
          columns={columns}
          rowKey="Id"
          rowSelection={rowSelection}
          pagination={{
            pageSize: 10,
            total: filteredContacts?.length,
            showSizeChanger: false,
          }}
          locale={{ emptyText: 'Ще немає даних для відображення' }}
          loading={loadingContacts}
          onRow={(record) => ({
            onClick: () => {
              if (isSelectionMode) {
                const hasCard = Boolean(record.RespCardNumber);
                const hasActiveCase = checkHasActiveCase(record, cases);
                if (hasCard || hasActiveCase) return; // disabled — ігноруємо клік
                setSelectedRowKeys((prev) =>
                  prev.includes(record.Id)
                    ? prev.filter((k) => k !== record.Id)
                    : [...prev, record.Id]
                );
              } else {
                push(`/contacts/edit/${record.Id}`);
              }
            },
            style: { cursor: isSelectionMode ? 'default' : 'pointer' },
          })}
        />
      </div>

      {/* Попередження: нерозподілені картки */}
      <Modal
        className="success-modal"
        visible={isUnassignedWarningVisible}
        onCancel={() => setIsUnassignedWarningVisible(false)}
        title={null}
        footer={
          <div className="ant-btn-wrapper">
            <Button text="Розподілити картки" bg="orange" onClick={() => setIsUnassignedWarningVisible(false)} />
            <Button text="Все одно замовити" bg="turquoise" onClick={handleUnassignedWarningOk} />
          </div>
        }
      >
        <div className="success-modal__wrapperSCA">
          <div className="text-black-medium-f22-l30" style={{ marginBottom: '12px' }}>
            Є нерозподілені картки
          </div>
          <div className="text-grey-dark-normal-f16-l28">
            На складі є <strong style={{ color: '#DB3EB1' }}>{unassignedFreeCards.length}</strong> карток,
            які ще не розподілені між співробітниками. Рекомендуємо спочатку розподілити
            існуючі картки, а потім замовляти нові.
          </div>
        </div>
      </Modal>

      {/* Підтвердження замовлення СКД */}
      <Modal
        className="success-modal"
        visible={isOrderModalVisible}
        onCancel={() => !isOrderLoading && setIsOrderModalVisible(false)}
        title={null}
        footer={
          <div className="ant-btn-wrapper">
            <Button text="Відмінити" bg="orange" onClick={() => setIsOrderModalVisible(false)} disabled={isOrderLoading} />
            <Button text="Замовити" bg="turquoise" onClick={handleConfirmOrder} loading={isOrderLoading} disabled={isOrderLoading} />
          </div>
        }
      >
        <div className="success-modal__wrapperSCA">
          <div className="text-black-medium-f22-l30" style={{ marginBottom: '16px' }}>
            Замовити СКД
          </div>

          {/* Список обраних сотрудників */}
          <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 16px 0', maxHeight: '180px', overflowY: 'auto', borderTop: '1px solid #f0f0f0' }}>
            {selectedContacts.map((contact) => (
              <li
                key={contact.Id}
                style={{ padding: '8px 0', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              >
                <span style={{ fontWeight: 500 }}>{contact.RespName}</span>
                {contact.RespEmployeeJobTitle && (
                  <span style={{ fontSize: '12px', color: '#8c8c8c', fontStyle: 'italic' }}>
                    {contact.RespEmployeeJobTitle}
                  </span>
                )}
              </li>
            ))}
          </ul>

          {/* Інфо про вартість і термін */}
          <div className="text-grey-dark-normal-f16-l28" style={{ fontSize: '15px', lineHeight: '24px', color: '#595959' }}>
            {(() => {
              const pricePerCard = reference?.PriceSCA?.price ?? 200;
              const total = pricePerCard * selectedContacts.length;
              const isMultiple = selectedContacts.length > 1;
              return (
                <>
                  Вартість виготовлення —{' '}
                  <strong style={{ color: '#DB3EB1' }}>{total} грн з ПДВ</strong>
                  {isMultiple && (
                    <span style={{ color: '#8c8c8c', fontSize: '13px' }}>
                      {' '}({pricePerCard} грн × {selectedContacts.length} осіб)
                    </span>
                  )}
                  .<br />
                  {isMultiple ? 'Картки будуть готові' : 'Картка буде готова'} протягом{' '}
                  <strong>5 робочих днів</strong> після оплати рахунку, який зʼявиться у Зверненні
                  на видачу {isMultiple ? 'карток' : 'картки'}.
                  <br /><br />
                  <span style={{ fontWeight: 600 }}>
                    Ви підтверджуєте замовлення {isMultiple ? 'нових карток' : 'нової картки'}?
                  </span>
                </>
              );
            })()}
          </div>
        </div>
      </Modal>

      {isTourActive && (
        <ContactsTour instructionsRead={instructionsRead} onFinish={markInstructionsAsRead} />
      )}
    </div>
  );
};

export default ContactsTable;
